import * as React from 'react';
import Text from '../../shared/Text';
import FormValidationMessage from './form-validation-message';
import { sendEmptyTextIfNull } from '../utils/input-handlers';
import {
  useOnRequestPasswordReset,
  useResetPasswordData,
  useSetResetPasswordData,
} from './providers/resetPassword/hooks';
import { useResetForm } from './providers/auth/hooks';
import { Box, Button, TextField, Typography } from '@mui/material';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from './shared-form-components';
import { AsyncButton } from '@c42/component-library';

const ForgotPassword: React.FC = () => {
  const resetForm = useResetForm();
  const onRequestPasswordReset = useOnRequestPasswordReset();
  const setResetPasswordData = useSetResetPasswordData();
  const { username, error, isCreatingPasswordReset: isSubmitting } = useResetPasswordData();

  const isUsernameEmpty = !username;
  const isUsernameInvalid = username != null && isUsernameEmpty;
  const isFormValid = !isUsernameEmpty;

  let usernameErrorText;
  if (isUsernameInvalid && isUsernameEmpty) {
    usernameErrorText = Text.get('_validate_username_or_email');
  }

  function submitHandler(e: React.FormEvent<any>) {
    e.preventDefault();
    onRequestPasswordReset(username);
  }

  function resetHandler(e: React.MouseEvent<any>) {
    e.preventDefault();
    resetForm();
  }

  const changeUsernameHandler: React.FormEventHandler<any> = (event) => {
    event.persist?.();
    setResetPasswordData((current) => ({
      ...current,
      username: (event.target as HTMLInputElement).value,
    }));
  };

  return (
    <FormWrapper>
      <form onSubmit={submitHandler} name="forgotPasswordFrm" noValidate data-test="forgot-password">
        <FormValidationMessage message={error} />

        <Box mt={4} textAlign="center">
          <Typography variant="h5">{Text.get('login_reset_password')}</Typography>
        </Box>

        <FormFieldWrapper>
          <TextField
            autoFocus
            autoComplete="off"
            disabled={isSubmitting}
            error={Boolean(usernameErrorText)}
            fullWidth
            helperText={usernameErrorText}
            inputProps={{ 'data-test': 'forgot-password-username' }}
            label={Text.get('_placeholder_username_or_email')}
            onBlur={sendEmptyTextIfNull.bind(null, changeUsernameHandler, username)}
            onChange={changeUsernameHandler}
            value={username || ''}
          />
        </FormFieldWrapper>

        <FormSubmitWrapper>
          <AsyncButton
            data-test="forgot-password-submit"
            disabled={isSubmitting || !isFormValid}
            isLoading={isSubmitting}
            type="submit"
            variant="contained"
          >
            {Text.get('login_reset_password')}
          </AsyncButton>
        </FormSubmitWrapper>

        <FormFieldWrapper>
          <Button
            data-test="forgot-password-back-to-signin"
            onClick={resetHandler}
            size="small"
            variant="text"
          >
            {Text.get('login_back_to_sign_in')}
          </Button>
        </FormFieldWrapper>
      </form>
    </FormWrapper>
  );
};

export default ForgotPassword;
